import React from 'react';
import { Col, Form, Row } from 'react-bootstrap';

import {
  CreateUpdateEstimateItemDto,
  GetPositionExtendedDto,
  GetProfileDto,
  GetProjectDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import FormRow from '../../../app/components/FormRow';
import PositionSelector from '../Selects/PositionSelector';
import ProfileCategorySelector from '../Selects/ProfileCategorySelector';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { NewDatePicker } from '../../../app/components/NewDatePicker';
import DictionarySelector from '../Selects/DictionarySelector';
import TeamSelector from '../Selects/TeamSelector';
import { Option } from '../Selects/Selector';
import { TooltipKey } from '../../../common/pokCore/tooltip.enum';

import {
  SpecialistTeamOptions,
  getPathPhaseIdForSpecialistTeam,
  hasPositionSpecialistTeams,
} from './SpecialistTeamPhaseUtils';

interface EstimateItemGeneralFormProps {
  estimateItem: CreateUpdateEstimateItemDto;
  project?: GetProjectDto;
  profile?: GetProfileDto;
  selectedPosition?: GetPositionExtendedDto;
  specialistTeamOptions: SpecialistTeamOptions;
  propertyChange: (obj: Partial<CreateUpdateEstimateItemDto>) => void;
}

const PathPhaseField: React.FC<{
  estimateItem: CreateUpdateEstimateItemDto;
  project?: GetProjectDto;
  propertyChange: (obj: Partial<CreateUpdateEstimateItemDto>) => void;
}> = ({ estimateItem, project, propertyChange }) => {
  const pok = usePokCore();
  const { t, tk } = useTranslation('projectLng');

  if (!project?.projectPathPhases?.length) {
    return null;
  }

  return (
    <FormRow
      controlId="pathPhase"
      label={t(tk.common.positionPhase)}
      tooltipKey={TooltipKey.positionPathPhaseTooltip}
    >
      <DictionarySelector
        dictionary="Faza"
        value={estimateItem?.pathPhaseDictionaryId}
        allowableValues={project.projectPathPhases.map(
          o => o.pathPhaseDictionary,
        )}
        onChange={option =>
          propertyChange({
            pathPhaseDictionaryId: option?.value,
          })
        }
        sortFunction={(a, b) =>
          pok.getLanguage() === 'PL'
            ? a.value.localeCompare(b.value)
            : a.valueEn?.localeCompare(b.valueEn || '') || 0
        }
      />
    </FormRow>
  );
};

// Pozycja z zespołami specjalistycznymi -> specialistTeamId (zespoły pozycji),
// bez -> deliveryTeamId (aktywne zespoły spółki kampanii). Dla użytkownika to jedno pole.
const SpecialistTeamField: React.FC<{
  estimateItem: CreateUpdateEstimateItemDto;
  project?: GetProjectDto;
  selectedPosition?: GetPositionExtendedDto;
  specialistTeamOptions: SpecialistTeamOptions;
  propertyChange: (obj: Partial<CreateUpdateEstimateItemDto>) => void;
}> = ({
  estimateItem,
  project,
  selectedPosition,
  specialistTeamOptions,
  propertyChange,
}) => {
  const { t, tk } = useTranslation('projectLng');

  if (!selectedPosition) {
    return null;
  }

  return (
    <FormRow
      controlId="specialistTeam"
      label={t(tk.estimates.deliveryTeam)}
      tooltipKey={TooltipKey.specialistTeamTooltip}
    >
      {hasPositionSpecialistTeams(selectedPosition.positionSpecialistTeams) ? (
        <TeamSelector
          key="specialist"
          allowableValues={specialistTeamOptions.teams}
          value={estimateItem?.specialistTeamId || undefined}
          preserveUnknownValue={true}
          clearable={true}
          onChange={option => {
            const newOption = option as Option;
            const specialistTeamId = newOption?.value || null;
            const pathPhaseDictionaryId = getPathPhaseIdForSpecialistTeam(
              selectedPosition.positionSpecialistTeams,
              specialistTeamId,
              project,
            );
            propertyChange({
              specialistTeamId,
              ...(pathPhaseDictionaryId ? { pathPhaseDictionaryId } : {}),
            });
          }}
        />
      ) : (
        <TeamSelector
          key="delivery"
          companyId={project?.company?.id}
          value={estimateItem?.deliveryTeamId || undefined}
          includeAssignedInactiveTeams={true}
          clearable={true}
          onChange={option => {
            const newOption = option as Option;
            propertyChange({ deliveryTeamId: newOption?.value || null });
          }}
        />
      )}
    </FormRow>
  );
};

const PositionFields: React.FC<EstimateItemGeneralFormProps> = props => {
  const { t, tk } = useTranslation('projectLng');
  const { estimateItem, project, profile, propertyChange } = props;

  return (
    <Form className="d-grid gap-4 mb-3">
      <FormRow controlId="startDate" label={t(tk.common.startDate)}>
        <NewDatePicker
          value={estimateItem?.startDate}
          dateChanged={startDate => propertyChange({ startDate })}
        />
      </FormRow>
      <FormRow
        controlId="ball"
        label={t(tk.estimates.ball)}
        tooltipKey={TooltipKey.ballTooltip}
      >
        <DictionarySelector
          dictionary="Produkt (Kulka)"
          value={estimateItem?.ballDictionaryId || ''}
          onChange={option =>
            propertyChange({
              ballDictionaryId: option?.value,
            })
          }
          clearable={true}
        />
      </FormRow>
      <PathPhaseField
        estimateItem={estimateItem}
        project={project}
        propertyChange={propertyChange}
      />
      <FormRow controlId="position" label={t(tk.common.profilePosition)}>
        <PositionSelector
          teamIds={
            project?.projectTeams
              ? project?.projectTeams.map(proTeam => proTeam.teamId)
              : []
          }
          profileId={profile?.id}
          purchaserId={project?.purchaser?.id}
          commission={profile?.commission}
          technicalCost={profile?.technicalCost}
          budgetId={project?.purchaser?.budget?.id}
          value={estimateItem?.positionId}
          onChange={option =>
            propertyChange({
              positionId: option?.value,
            })
          }
        />
      </FormRow>
      <SpecialistTeamField
        estimateItem={estimateItem}
        project={project}
        selectedPosition={props.selectedPosition}
        specialistTeamOptions={props.specialistTeamOptions}
        propertyChange={propertyChange}
      />
    </Form>
  );
};

const ProfileFields: React.FC<EstimateItemGeneralFormProps> = props => {
  const { t, tk } = useTranslation('projectLng');
  const { estimateItem, project, profile, selectedPosition, propertyChange } =
    props;

  return (
    <Form className="d-grid gap-4 mb-3">
      <FormRow controlId="endDate" label={t(tk.common.endDate)}>
        <NewDatePicker
          value={estimateItem?.endDate}
          dateChanged={endDate => propertyChange({ endDate })}
        />
      </FormRow>
      <FormRow controlId="profileCat" label={t(tk.estimates.category)}>
        <ProfileCategorySelector
          autoSelect={true}
          profileId={profile?.id || ''}
          value={estimateItem?.profileCategoryId}
          onChange={option =>
            propertyChange({
              profileCategoryId: option?.value,
            })
          }
        />
      </FormRow>
      <FormRow controlId="country" label={t(tk.projects.country)}>
        <DictionarySelector
          dictionary="Kraj/rynek"
          value={estimateItem?.countryDictionaryId}
          allowableValues={
            project?.projectCountries?.map(o => o.countryDictionary) || []
          }
          onChange={option =>
            propertyChange({
              countryDictionaryId: option?.value,
            })
          }
        />
      </FormRow>
      <FormRow
        controlId="communicationChannel"
        label={t(tk.common.communicationChannel)}
      >
        <DictionarySelector
          dictionary="Kanały komunikacji"
          value={selectedPosition?.dictionary.id}
          readOnly={true}
        />
      </FormRow>
    </Form>
  );
};

export const EstimateItemGeneralForm: React.FC<
  EstimateItemGeneralFormProps
> = props => {
  return (
    <Row className="mt-3">
      <Col className="col-lg-6 col-12 mb-1">
        <PositionFields {...props} />
      </Col>
      <Col className="col-lg-6 col-12 mb-1">
        <ProfileFields {...props} />
      </Col>
    </Row>
  );
};
